import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const ServiceCategories = () => {
  const navigate = useNavigate();
  const services = [
    {
      category: "Emergency Services",
      icon: "Siren",
      color: "error",
      services: [
        {
          name: "Burst Pipe Repair",
          description: "Immediate response to burst pipes with professional repair and damage prevention.",
          features: ["24/7 Availability", "Under 60min Response", "Damage Assessment"]
        },
        {
          name: "Emergency Leak Detection",
          description: "Advanced leak detection technology to locate hidden leaks quickly and accurately.",
          features: ["Thermal Imaging", "Acoustic Detection", "Minimal Disruption"]
        },
        {
          name: "Drain Blockage Clearing",
          description: "Fast clearing of blocked drains and sewers using professional equipment.",
          features: ["High-Pressure Jetting", "CCTV Inspection", "Root Removal"]
        }
      ]
    },
    {
      category: "Leak Detection & Repair",
      icon: "Droplets",
      color: "primary",
      services: [
        {
          name: "Hidden Leak Detection",
          description: "Non-invasive detection of leaks behind walls, under floors, and in ceilings.",
          features: ["Moisture Mapping", "Pressure Testing", "Detailed Reporting"]
        },
        {
          name: "Pipe Repair & Replacement",
          description: "Expert repair or replacement of damaged pipes with quality materials.",
          features: ["Copper & PVC", "Trenchless Options", "Warranty Included"]
        },
        {
          name: "Slab Leak Repair",
          description: "Specialized repair of leaks in concrete slab foundations.",
          features: ["Minimal Excavation", "Foundation Protection", "Long-term Solutions"]
        }
      ]
    },
    {
      category: "Installation Services",
      icon: "Wrench",
      color: "accent",
      services: [
        {
          name: "Fixture Installation",
          description: "Professional installation of sinks, toilets, showers, and bathtubs.",
          features: ["Quality Fixtures", "Proper Sealing", "Code Compliance"]
        },
        {
          name: "Water Heater Installation",
          description: "Expert installation of gas and electric water heaters with proper venting.",
          features: ["Energy Efficient", "Safety Certified", "Maintenance Plans"]
        },
        {
          name: "Pipe System Installation",
          description: "Complete plumbing system installation for new construction and renovations.",
          features: ["Design Consultation", "Quality Materials", "Project Management"]
        }
      ]
    },
    {
      category: "Maintenance & Inspection",
      icon: "ClipboardCheck",
      color: "success",
      services: [
        {
          name: "Preventive Maintenance",
          description: "Regular maintenance to prevent costly repairs and extend system life.",
          features: ["Annual Inspections", "Cleaning Services", "Performance Testing"]
        },
        {
          name: "Plumbing Inspections",
          description: "Comprehensive inspections for home purchases, renovations, or peace of mind.",
          features: ["Detailed Reports", "Photo Documentation", "Repair Estimates"]
        },
        {
          name: "Water Quality Testing",
          description: "Testing and treatment solutions for water quality issues.",
          features: ["Contamination Testing", "Filtration Systems", "Water Softeners"]
        }
      ]
    }
  ];

  const colorClasses = {
    error: "bg-error/10 text-error border-error",
    primary: "bg-primary/10 text-primary border-primary",
    accent: "bg-accent/10 text-accent border-accent",
    success: "bg-success/10 text-success border-success"
  };

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-headline text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Comprehensive Plumbing Solutions
          </h2>
          <p className="font-body text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            From emergency repairs to complete installations, we provide expert plumbing services backed by 30+ years of experience.
          </p>
        </div>

        <div className="space-y-12 md:space-y-16">
          {services?.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              <div className="flex items-center gap-4 mb-6 md:mb-8">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border-2 ${colorClasses?.[category?.color]}`}>
                  <Icon name={category?.icon} size={32} color="currentColor" />
                </div>
                <div>
                  <h3 className="font-headline text-2xl md:text-3xl font-bold text-foreground">
                    {category?.category}
                  </h3>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {category?.services?.map((service, serviceIndex) => (
                  <div 
                    key={serviceIndex}
                    className="bg-card rounded-xl p-6 md:p-8 border border-border hover:border-primary hover:shadow-lg transition-all duration-300"
                  >
                    <h4 className="font-headline text-xl font-semibold text-foreground mb-3">
                      {service?.name}
                    </h4>
                    <p className="font-body text-muted-foreground mb-6">
                      {service?.description}
                    </p>
                    <div className="space-y-2">
                      {service?.features?.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-2">
                          <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                          <span className="text-sm text-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 bg-gradient-to-br from-primary to-secondary rounded-2xl p-8 md:p-12 text-center text-primary-foreground">
          <h3 className="font-headline text-2xl md:text-3xl font-bold mb-4">
            Not Sure Which Service You Need?
          </h3>
          <p className="font-body text-lg opacity-90 mb-6 max-w-2xl mx-auto">
            Our expert team can help diagnose your plumbing issue and recommend the best solution. Call us for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:0827935785"
              className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-4 rounded-lg font-semibold transition-colors emergency-cta"
            >
              <Icon name="Phone" size={20} />
              <span>Call: 082 793 5785</span>
            </a>
            <button
              onClick={() => navigate('/contact')}
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 px-8 py-4 rounded-lg font-semibold transition-colors">
              <Icon name="MessageCircle" size={20} />
              <span>Request Callback</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceCategories;